import { Grid, IconButton, useMediaQuery } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import globalStyles from "../../../assets/styles/global.module.scss";
import styles from "../../../assets/styles/componentStyles/dataconnector.module.scss";
import { GlobalTable } from '../../utility/globalTable';
import { DataConnectorAssignment, dataConnectorList } from '../../../models/axiosModel';
import GlobalPopUp from '../../utility/globalPopUp';
import AddIcon from '@mui/icons-material/Add';
import deleteDoc from "../../../assets/deleteDoc.svg";
import GlobalDeletePopup from '../../utility/globalDeletePopup/globalDeletePopup';
import GlobalButton from '../../utility/globalButton/button';
import { DataConnectorContext, DataConnectorContextpayload } from '../../../context/dataConnectorContext';
import { hasVoiceAgentManagePermission, isSystemApi } from '../../../utils/constants';
import { useGenericStore } from '../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';
import AssignCustomApiToVoiceAgentPopup from './assignCustomApiToVoiceAgentPopup';
import { getVoiceAgentAssignmentUniqueId, isVoiceAgentDataConnectorAssignment } from '../customApi/assignVoiceAgentPopup';
import useGenericHook from '../../../hooks/useGenericHook';

const VoiceAgentCustomApi = () => {
    const headerList = [
        {
            name: "Name",
            alignment: "left",
            flagIcon: false,
            valueKey: "data_connector_function_name",
        },
        {
            name: "Description",
            alignment: "left",
            flagIcon: false,
            valueKey: "data_connector_function_description",
        },
        {
            name: "URL",
            alignment: "left",
            flagIcon: false,
            valueKey: "chatbot_data_connector_url",
        },
        {
            name: "Method Name",
            alignment: "left",
            flagIcon: false,
            valueKey: "function_method_name",
        },
        {
            name: "",
            alignment: "right",
            flagIcon: true,
            valueKey: "",
            iconKey: "editIcon"
        },
    ]

    const navigate = useNavigate()
    const [searchParams] = useSearchParams();
    const voiceAgentId = searchParams.get("voice_agent_id") || "";

    const { apiRoutesPermissionAssignment, userDetail, flagLoading, setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;
    const { getChatbotDataConnectorList, getDataConnectorAssignmentType, removeDataConnectorAssignment } = useContext(DataConnectorContext) as DataConnectorContextpayload;

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const [assignedCustomApis, setAssignedCustomApis] = useState<dataConnectorList[]>([]);
    const [flagRenderData, setFlagRenderData] = useState<Boolean | null>(null);
    const [flagDeleteDocumentDataPopup, setFlagDeleteDocumentDataPopup] = useState(false)
    const [documentSelected, setDocumentSelected] = useState<null | dataConnectorList>(null)
    const [flagAssignDataConnectorPopup, setFlagAssignDataConnectorPopup] = useState<boolean>(false);
    const [rowAllowEdit] = useState(null);

    const flagSmallScreen = useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1000px)');
    const { isScreenBelow600 } = useGenericHook();
    const canManage = hasVoiceAgentManagePermission(apiRoutesPermissionAssignment, userDetail);

    const getAssignedCustomApis = async () => {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
        const response = await getChatbotDataConnectorList();

        if (response.status && response.data) {
            const customAPIs = response.data
                .filter((data: dataConnectorList) =>
                    data.data_connector_assignment?.some(
                        (assignment: DataConnectorAssignment) =>
                            isVoiceAgentDataConnectorAssignment(assignment)
                            && getVoiceAgentAssignmentUniqueId(assignment) === voiceAgentId
                    )
                )
                .map((dataConnector: dataConnectorList) => ({ ...dataConnector, ...dataConnector.data_connector_functions }))

            const updatedCustomAPI = customAPIs?.map((api) => {
                if (isSystemApi(api?.chatbot_data_connector_function_path)) {
                    return {
                        ...api,
                        data_connector_function_description: "-",
                        chatbot_data_connector_url: "-",
                        function_method_name: "-",
                    }
                }
                return api;
            })
            setAssignedCustomApis(updatedCustomAPI)
        } else {
            handleError(response);
            setAssignedCustomApis([])
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }))
    }

    const getDataConnectorAssignmentTypeApi = async () => {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
        const res = await getDataConnectorAssignmentType();
        if (!(res.status)) {
            handleError(res);
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }))
    }

    useEffect(() => {
        if (!voiceAgentId) {
            navigate('/voiceagent');
            return;
        }
        setFlagRenderData(false);
        (async () => {
            await getDataConnectorAssignmentTypeApi();
            await getAssignedCustomApis();
            setFlagRenderData(true);
        })();
    }, [voiceAgentId]);

    const deleteDocumentDataPopupOpen = (data: dataConnectorList) => {
        setDocumentSelected(data)
        setFlagDeleteDocumentDataPopup(true)
    }

    const rowClicked = async (e: any, rowDetail: any) => {
        if (e.iconKey !== "editIcon" && !isSystemApi(rowDetail?.chatbot_data_connector_function_path)) {
            navigate(`/customAPI/createCustomAPI?connector_id=${rowDetail.chatbot_data_connector_id}`)
        }
    }

    const handleRemoveDataConnectorAssignment = async () => {
        setFlagLoading({ ...flagLoading, flagRoundLoader: true });
        if (documentSelected) {
            const data_connector_assignement_id = documentSelected.data_connector_assignment?.find(
                (assignment: DataConnectorAssignment) =>
                    isVoiceAgentDataConnectorAssignment(assignment)
                    && getVoiceAgentAssignmentUniqueId(assignment) === voiceAgentId
            )?.data_connector_assignment_id;
            if (data_connector_assignement_id) {
                const res = await removeDataConnectorAssignment(data_connector_assignement_id);
                if (res.status) {
                    showSuccess("Custom API Removed Successfully")
                    await getAssignedCustomApis();
                } else {
                    handleError(res);
                }
            }
        }
        setFlagLoading({ ...flagLoading, flagRoundLoader: false });
    }

    const menuItems = canManage ? [
        { label: 'Remove', icon: <img src={deleteDoc} alt="delete" />, action: (e: dataConnectorList) => deleteDocumentDataPopupOpen(e) },
    ] : [];

    const innerHeight = (window.innerHeight - 190);

    return (
        <>
            {flagDeleteDocumentDataPopup &&
                <GlobalDeletePopup
                    deleteDescription={`Are you sure you want to remove the "${documentSelected?.data_connector_functions?.data_connector_function_name || ""}" custom API`}
                    title="Remove Confirmation"
                    primaryButtonText="Remove"
                    open={flagDeleteDocumentDataPopup}
                    onClose={() => {
                        setFlagDeleteDocumentDataPopup(false)
                    }}
                    onDelete={() => {
                        handleRemoveDataConnectorAssignment();
                        setFlagDeleteDocumentDataPopup(false)
                    }}
                />
            }

            {flagAssignDataConnectorPopup &&
                <Grid item className={globalStyles.fullwidth}>
                    <GlobalPopUp
                        open={flagAssignDataConnectorPopup}
                        onClose={() => { setFlagAssignDataConnectorPopup(false) }}
                        paperMinWidth={isScreenBelow600 ? "340px" : "550px"}
                        paperMaxwidth={isScreenBelow600 ? "610px" : "600px"}
                        flagShowCloseIcon={true}
                        handleClose={() => { setFlagAssignDataConnectorPopup(false) }}
                        borderRadius='10px'
                        titlePadding='0px'
                    >
                        <AssignCustomApiToVoiceAgentPopup
                            getAssignedCustomApis={getAssignedCustomApis}
                            setFlagAssignDataConnectorPopup={setFlagAssignDataConnectorPopup}
                        />
                    </GlobalPopUp>
                </Grid>
            }

            <Grid item xl={12} xs={12} className={`${globalStyles.fullwidth}`} sx={{ width: "100%", paddingTop: "20px" }}>
                <Grid container alignItems={"center"} justifyContent={"end"} >
                    <Grid item xs={12} className={`${globalStyles.globalHeadRightSubItem} ${styles.globalAddConnectorButton}`}>
                        <Grid container sx={{ alignItems: "center" }} justifyContent={"end"}>
                            {canManage && window.screen.width >= 900 &&
                                <Grid item className={`${globalStyles.globalNewAddBtnItem}`} sx={{ textAlign: "end" }}>
                                    <GlobalButton
                                        buttonText="Assign"
                                        startIcon={<AddIcon />}
                                        className='primaryButtonStyle'
                                        sx={{ width: "120px" }}
                                        onClick={() => setFlagAssignDataConnectorPopup(true)}
                                    />
                                </Grid>
                            }
                            {canManage && window.screen.width <= 899 &&
                                <IconButton
                                    className={`${globalStyles.cmpGlobalMobileAddIcon}`}
                                    onClick={() => { setFlagAssignDataConnectorPopup(true) }}
                                >
                                    <AddIcon />
                                </IconButton>
                            }
                        </Grid>
                    </Grid>

                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12} className={`${styles.documentsTableItem}`} mt={2} height={`${innerHeight}px !important`}>
                        <GlobalTable
                            headerList={headerList}
                            rowData={assignedCustomApis}
                            tableHeight={"76vh"}
                            tableContainerMinHeight={"auto"}
                            onCellClick={(e: any, rowDetail: any) => { rowClicked(e, rowDetail) }}
                            onRowClick={(e: any) => { console.log(e) }}
                            deleteRowData={deleteDocumentDataPopupOpen}
                            rowAllowEdit={rowAllowEdit}
                            flagPagination
                            menuItemsFromParent={menuItems}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default VoiceAgentCustomApi
